import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { generateProductDescription } from "../../../utils/Product";
import Skeleton from "react-loading-skeleton";
import { Form } from "react-bootstrap";
import BaseEmailSection from "../Common/BaseEmailSection";
import EmailImageUploader from "../Common/EmailImageUploader";
import EmailTextEditor from "../Common/EmailTextEditor";
import { useEmailStyles } from "../../../components/Context/EmailStyleContext";

const LeftAlignImage = ({
    id, 
    data, 
    onUpdate,
    previewMode = false
}) => {
    // Get styling from context
    const { styles } = useEmailStyles();
    
    // Log styles to verify they're being received correctly
    useEffect(() => {
        console.log('LeftAlignImage: Current styles', styles);
    }, [styles]);

    // Extract data with fallbacks
    const extractData = () => {
        const title = data?.title || data?.heading || "" ;
        const description = data?.description || data?.text || data?.content || "";
        const image = data?.image || data?.img || data?.src || null;
        const buttonText = data?.button_text || data?.cta || "";
        const buttonLink = data?.button_link || data?.url || data?.href || "#";
        const imageWidth = data?.image_width || data?.imgWidth || "40%";
        
        return { title, description, image, buttonText, buttonLink, imageWidth };
    };

    // Extract initial values
    const { 
        title: initialTitle,
        description: initialDescription,
        image: initialImage,
        buttonText: initialButtonText,
        buttonLink: initialButtonLink,
        imageWidth: initialImageWidth
    } = extractData();
    
    // State hooks
    const [title, setTitle] = useState(initialTitle);
    const [description, setDescription] = useState(initialDescription);
    const [contentImage, setContentImage] = useState(initialImage);
    const [ctaText, setCtaText] = useState(initialButtonText);
    const [ctaLink, setCtaLink] = useState(initialButtonLink);
    const [imgWidth, setImgWidth] = useState(initialImageWidth);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const location = useLocation();
    const initialSku = location.state?.sku || "";

    // Update local state when props change
    useEffect(() => {
        const { title, description, image, buttonText, buttonLink, imageWidth } = extractData();
        setTitle(title);
        setDescription(description);
        setContentImage(image);
        setCtaText(buttonText);
        setCtaLink(buttonLink);
        setImgWidth(imageWidth);
    }, [data]);

    // If no description is available, generate one on component mount
    useEffect(() => {
        if (!description && initialSku) {
            handleProductRegenerate("benefit", "");
        }
    }, []);

    const handleProductRegenerate = async (section, furtherDetails) => {
        try {
            setIsLoading(true);
            const response = await generateProductDescription(initialSku, '', section, furtherDetails);
            const newTitle = response.data.benefit || "";
            const newDescription = response.data.description || "";
            
            setTitle(newTitle);
            setDescription(newDescription);

            // Update parent with new data
            updateParentData({ title: newTitle, description: newDescription });
        } catch (error) {
            console.error("Regeneration failed:", error);
            setError(error.message || "Failed to regenerate content");
        } finally {
            setIsLoading(false);
        }
    };

    // Helper to update the parent component data
    const updateParentData = (updates = {}) => {
        if (onUpdate) {
            onUpdate({
                ...data,
                component_name: "LeftAlignImage",
                title: updates.title !== undefined ? updates.title : title,
                description: updates.description !== undefined ? updates.description : description,
                image: updates.image !== undefined ? updates.image : contentImage,
                button_text: updates.ctaText !== undefined ? updates.ctaText : ctaText,
                button_link: updates.ctaLink !== undefined ? updates.ctaLink : ctaLink,
                image_width: updates.imgWidth !== undefined ? updates.imgWidth : imgWidth
            }, id);
        }
    };

    // Handlers
    const handleTitleChange = (newTitle) => {
        setTitle(newTitle);
        updateParentData({ title: newTitle });
    };

    const handleDescriptionChange = (newDescription) => {
        setDescription(newDescription);
        updateParentData({ description: newDescription });
    };

    const handleImageChange = (newImage) => {
        setContentImage(newImage);
        updateParentData({ image: newImage });
    };

    const handleCtaTextChange = (e) => {
        setCtaText(e.target.value);
        updateParentData({ ctaText: e.target.value });
    };

    const handleCtaLinkChange = (e) => {
        setCtaLink(e.target.value);
        updateParentData({ ctaLink: e.target.value });
    };

    const handleImgWidthChange = (e) => {
        setImgWidth(e.target.value);
        updateParentData({ imgWidth: e.target.value });
    };

    const handleRemove = () => {
        if (onUpdate && typeof onUpdate === 'function') {
            // Call with null to indicate removal
            onUpdate(null, id);
        }
    };

    const toggleEditing = () => {
        setIsEditing(!isEditing);
    };

    // Apply styles from context or use fallbacks
    const textStyle = {
        fontFamily: styles.fontFamily || 'helvetica, "helvetica neue", arial, verdana, sans-serif',
        color: styles.primaryColor || '#333333'
    };
    
    const headingStyle = {
        fontFamily: styles.fontFamily || 'helvetica, "helvetica neue", arial, verdana, sans-serif',
        color: styles.secondaryColor || '#a1894c',
        fontSize: "22px", 
        marginBottom: "10px"
    };
    
    const buttonStyle = {
        backgroundColor: styles.secondaryColor || '#a1894c', 
        color: styles.buttonTextColor || '#ffffff',
        borderRadius: '4px',
        padding: '8px 16px',
        display: 'inline-block',
        textDecoration: 'none',
        fontSize: '14px'
    };

    // Render edit mode
    if (isEditing && !previewMode) {
        return (
            <BaseEmailSection
                id={id}
                title="Left Image with Content"
                onRemove={handleRemove}
                onEdit={toggleEditing}
                previewMode={previewMode}
            >
                <div className="p-3 rounded bg-white">
                    {/* Title Editor */}
                    <EmailTextEditor
                        content={title}
                        onContentChange={handleTitleChange}
                        onRegenerate={() => handleProductRegenerate("benefit", "")}
                        allowRegenerate={true}
                        isLoading={isLoading}
                        type="heading"
                        label="Title"
                        placeholder="Enter section title"
                        previewMode={previewMode}
                        className="mb-3"
                    />
                    
                    {/* Description Editor */}
                    <EmailTextEditor
                        content={description}
                        onContentChange={handleDescriptionChange}
                        onRegenerate={() => handleProductRegenerate("description", "")}
                        allowRegenerate={true}
                        isLoading={isLoading}
                        label="Content"
                        placeholder="Enter section content"
                        rows={3}
                        previewMode={previewMode}
                        className="mb-3"
                    />
                    
                    {/* Image Uploader */}
                    <EmailImageUploader
                        initialImage={contentImage}
                        onImageUpload={handleImageChange}
                        allowRegenerate={true}
                        onRegenerate={() => console.log("Image regeneration not implemented")}
                        label="Image"
                        height="200px"
                        previewMode={previewMode}
                        className="mb-3"
                    />
                    
                    {/* Advanced Settings */}
                    <div className="mb-3">
                        <Form.Label>Image Width</Form.Label>
                        <Form.Select 
                            value={imgWidth} 
                            onChange={handleImgWidthChange}
                        >
                            <option value="30%">Small (30%)</option>
                            <option value="40%">Medium (40%)</option>
                            <option value="50%">Large (50%)</option>
                        </Form.Select>
                    </div>
                    
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <Form.Label>Button Text</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Button text (optional)"
                                value={ctaText}
                                onChange={handleCtaTextChange}
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <Form.Label>Button Link</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="https://example.com"
                                value={ctaLink}
                                onChange={handleCtaLinkChange}
                            />
                        </div>
                    </div>

                    <button className="btn btn-primary mt-2" onClick={toggleEditing}>
                        Done
                    </button>
                </div>
            </BaseEmailSection>
        );
    }

    // Render view mode
    return (
        <BaseEmailSection
            id={id}
            title="Left Image with Content"
            onRemove={!previewMode ? handleRemove : undefined}
            onEdit={!previewMode ? toggleEditing : undefined}
            previewMode={previewMode}
        >
            <div className="row">
                <div className="col-md-5" style={{ width: imgWidth }}>
                    <div className="rounded p-2 d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
                        {contentImage ? (
                            <img src={contentImage} alt="Feature" className="img-fluid rounded" />
                        ) : (
                            <div className="d-flex align-items-center justify-content-center h-100 outline-none rounded">
                                <img src="/img-preview.svg" alt="Feature placeholder" className="img-fluid rounded" />
                            </div>
                        )}
                    </div>
                </div>
                <div className="col-md-7" style={{ width: `calc(100% - ${imgWidth})` }}>
                    <div>
                        <h3 style={headingStyle}>
                            {title}
                        </h3>
                        <p className="description-text" style={textStyle}>
                            {description}
                        </p>
                        
                        {ctaText && (
                            <a 
                                href={ctaLink || "#"} 
                                className="btn mt-2 email-cta" 
                                style={buttonStyle}
                            >
                                {ctaText}
                            </a>
                        )}
                    </div>
                </div>
            </div>
        </BaseEmailSection>
    );
};

export default LeftAlignImage;
